/**
 * `/desks/:deskId`: the Team queue, the home of a Team's work.
 *
 *   header   the Team's name; where its tickets come from (the source line),
 *            how many repositories it works on, where it posts; Add tickets
 *            and the settings gear on the right
 *   chips    when it picks, how many a day, which gates a person still holds,
 *            how hard it reviews; and the one fact worth a glance, how many it
 *            picked today and when it picks next
 *   groups   Needs you, Ready to merge, Working, Stopped, Done
 *            (`ticket/queueGroups.ts`); Done holds every finished ticket,
 *            20 a page (`?done=`), with how many finished today; a split's
 *            tickets print under their parent's row (YAG-1113)
 *   find     the row under the chips: the search box (`?find=`) keeps each
 *            group's matches and adds the archived ones, which print nowhere
 *            else, as Archived; the Status filter beside it (`?status=`)
 *            keeps only the chosen groups, each printed even when empty, and
 *            Archived among them reaches the archive without a search. A
 *            search that finds nothing here hands its words to Search (⌘K)
 *
 * A row opens the ticket sheet (`?item=`); Add tickets opens its own sheet
 * (`?add=1`, with its own search in `?q=`). Rows never commit anything.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Search, Settings } from "lucide-react";
import {
  parseWorkSearch,
  type JobAuthority,
  type JobClockTrigger,
  type ReviewLevel,
  type TeamPickedToday,
  type WorkItem,
} from "@yagni/shared";
import {
  Btn,
  Chip,
  Group,
  Groups,
  ListHead,
  Pager,
  PlusIcon,
  type GroupTone,
} from "../queue/index.js";
import { useDockBinding } from "../shell/askDockStore.js";
import { teamScope } from "../shell/yagniRailScopes.js";
import { useAuthSession, useCan, useIsWorkspaceAdmin } from "../hooks/useWorkspaceRole.js";
import { SUBSCRIPTIONS_CHANGED_EVENT } from "../shell/useNavDesks.js";
import { useWorkFeed } from "../work/useWorkFeed.js";
import { useWorkerLookup } from "../work/useWorkerLookup.js";
import { useTeamIdentity } from "../work/useTeamIdentity.js";
import { foldPipelineRows, driverOf, type PipelineRow } from "../work/workViews.js";
import { SurfaceSkeleton } from "../ui/index.js";
import {
  QUEUE_GROUP_LABEL,
  TEAM_DONE_PAGE_SIZE,
  archivedRows,
  isSettledToday,
  needsOrderOf,
  queueGroupOf,
  rowMatchesSearch,
  searchQueueGroups,
  teamQueueGroups,
  type QueueGroup,
  type QueuePart,
} from "../ticket/queueGroups.js";
import { TicketEntry, TicketRow } from "../ticket/TicketRow.js";
import { useQueueSheet } from "../ticket/useQueueSheet.js";
import { StatusFilterMenu } from "../ticket/StatusFilterMenu.js";
import { searchStore } from "../shell/searchStore.js";
import { useStatusFilter } from "./useStatusFilter.js";
import { AddTicketsSheet } from "../ticket/AddTicketsSheet.js";
import { REVIEW_LEVEL_WORD, lineOf } from "./allTeams.js";
import { BACKLOG_SOURCE_WORD, FILTER_MAX, backlogCardTitle, truncatedQueryWords } from "./copy.js";
import { filterTextOf } from "./backlogFilterText.js";
import { formatScheduleSlot } from "./jobSentence.js";
import { TeamSettingsSheet } from "./TeamSettingsSheet.js";
import { TeamRunningSystem } from "../connections/system/RunningSystem.js";
import {
  SETTINGS_CONNECTIONS_ANCHOR,
  SETTINGS_PEOPLE_ANCHOR,
  sectionFor,
  type SettingsSection,
} from "./settings/settingsDraft.js";
import { connectionLabel } from "./copy.js";
import { useTeamReads } from "./useTeamReads.js";
import { WhatBaileySees } from "./WhatBaileySees.js";
import { nothingToPickLine, usePickList } from "./PickList.js";
import { OffTicketWork } from "./OffTicketWork.js";
import { importOutcomeLine, importOutcomeOf, trackerName } from "./importOutcome.js";
import {
  attachedBacklogs,
  attachedRepos,
  useTeamPage,
  type TeamDeclaredSystem,
} from "./useTeamPage.js";
import { PeopleStack } from "./people/PeopleStack.js";
import { AddPeopleDialog } from "./people/AddPeopleDialog.js";
import { canManagePeople } from "./people/people.js";
import { captureUiError } from "../sentry.js";
import "./teams.css";

/** The filter cut at the end, on a whole token, never mid-word. */
export function truncateFilter(filter: string, max = FILTER_MAX): string {
  return truncatedQueryWords(filter, max);
}

function authorityWord(authority: JobAuthority | null | undefined): string {
  return authority === "autonomous" ? "runs" : "asks you";
}

/** "tomorrow 09:00", "today 17:00", "Oct 14 09:00": when the propose line picks next. */
export function nextPickWords(iso: string | null, now: Date = new Date()): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(at).setHours(0, 0, 0, 0) - day.getTime()) / 86_400_000);
  if (diff === 0) return `today ${time}`;
  if (diff === 1) return `tomorrow ${time}`;
  return `${at.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${time}`;
}

/** "2 of 3 picked today · next pick tomorrow 09:00". Null when the Team has no propose line. */
export function pickedTodayLine(
  picked: TeamPickedToday | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!picked) return null;
  const count =
    picked.cap != null
      ? `${picked.count} of ${picked.cap} picked today`
      : `${picked.count} picked today`;
  const next = nextPickWords(picked.nextPickAt, now);
  return next ? `${count} · next pick ${next}` : count;
}

/** The match count for a Team's tracker filter, asked once per filter. */
function useFilterMatches(
  deskId: string,
  filter: string | null,
  source: string | null,
): number | null {
  const [total, setTotal] = useState<number | null>(null);
  useEffect(() => {
    setTotal(null);
    if (!filter || (source !== "jira" && source !== "linear")) return;
    let cancelled = false;
    fetch(`/api/desks/${encodeURIComponent(deskId)}/backlog/preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filter, source }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`backlog preview failed (${res.status})`);
        return res.json() as Promise<{ total?: unknown; kind?: unknown }>;
      })
      .then((body) => {
        if (!cancelled && body && typeof body.total === "number" && body.kind !== "error")
          setTotal(body.total);
      })
      .catch((err: unknown) => {
        captureUiError(err, {
          area: "Teams",
          operation: "read_filter_matches",
          deskId,
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [deskId, filter, source]);
  return total;
}

/** A ticket an Agent put on the queue since `since`: Bailey's picks, not a review or a Harper post. */
export function proposedSince(item: WorkItem, since: number): boolean {
  if (item.originType !== "agent" || item.kind === "review" || item.kind === "harper_job")
    return false;
  return new Date(item.createdAt).getTime() >= since;
}

/** The page a `?done=` (or the Archived pager) asks for, clamped to the list. */
export function pageOf(raw: string | number | null, total: number): number {
  const last = Math.max(1, Math.ceil(total / TEAM_DONE_PAGE_SIZE));
  const page = Math.floor(Number(raw));
  return Number.isFinite(page) && page > 1 ? Math.min(page, last) : 1;
}

/** Needs you's dashed line: the queue's own, or what a search found elsewhere. */
export function needsEmptyLine(searching: boolean, anyMatch: boolean, anyWorking: boolean): string {
  if (!searching) {
    return `Nothing is waiting on you. ${anyWorking ? "Work is moving on its own." : "Add tickets to give the Team something to do."}`;
  }
  if (anyMatch) return "Nothing waiting on you matches.";
  return "No ticket on this Team matches. The search covers finished and archived tickets too.";
}

interface PagedGroupProps {
  label: string;
  tone?: GroupTone;
  rows: readonly PipelineRow[];
  /** The page asked for; clamped to the rows here. */
  page: string | number | null;
  onPage: (page: number) => void;
  /** What the arrows move, for a screen reader. */
  pagerLabel: string;
  /** A fact before the pager in the header (Done's "N today"). */
  fact?: ReactNode;
  /** Printed dashed when there are no rows; without it the group is omitted. */
  empty?: ReactNode;
  renderRow: (row: PipelineRow) => ReactNode;
}

/** A group 20 rows a page, the pager in its header and again under its last row. */
function PagedGroup({
  label,
  tone,
  rows,
  page,
  onPage,
  pagerLabel,
  fact,
  empty,
  renderRow,
}: PagedGroupProps) {
  const at = pageOf(page, rows.length);
  const pager = (
    <Pager
      page={at}
      pageSize={TEAM_DONE_PAGE_SIZE}
      total={rows.length}
      onPage={onPage}
      label={pagerLabel}
    />
  );
  return (
    <Group
      label={label}
      tone={tone}
      count={rows.length}
      aside={
        <>
          {fact}
          {pager}
        </>
      }
      foot={rows.length > TEAM_DONE_PAGE_SIZE ? <div className="pagefoot">{pager}</div> : undefined}
      empty={empty}
    >
      {rows.slice((at - 1) * TEAM_DONE_PAGE_SIZE, at * TEAM_DONE_PAGE_SIZE).map(renderRow)}
    </Group>
  );
}

/**
 * The Team's declared system (YAG-1109): its name and every member, the shared
 * ones and the one whose compose file starts the rest marked, and where to
 * change it for a person who may.
 */
export function TeamSystemBlock({
  system,
  canEdit,
}: {
  system: TeamDeclaredSystem;
  canEdit: boolean;
}) {
  return (
    <section
      className="team-system-block"
      aria-label={`System ${system.name}`}
      data-testid="team-system-block"
    >
      <div className="team-system-head">
        <span>
          System <b>{system.name}</b>
        </span>
        {canEdit && (
          <Link to="/settings/systems" className="team-system-edit">
            Edit under Settings
          </Link>
        )}
      </div>
      {system.repos.length > 0 ? (
        <ul className="team-system-repos" aria-label="Repositories in the system">
          {system.repos.map((repo) => (
            <li key={repo.fullName}>
              <span className="team-system-repo">{repo.fullName}</span>
              {repo.compose && <Chip>compose</Chip>}
              {repo.shared && <Chip>shared</Chip>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="team-system-empty">The system's repositories could not be read.</p>
      )}
    </section>
  );
}

export function TeamQueueRoute() {
  const { deskId } = useParams<{ deskId: string }>();
  if (!deskId) return <Navigate to="/waiting" replace />;
  return <TeamQueue key={deskId} deskId={deskId} />;
}

function TeamQueue({ deskId }: { deskId: string }) {
  const [params, setParams] = useSearchParams();
  const viewerUserId = useAuthSession()?.user?.id ?? null;
  const team = useTeamPage(deskId);
  const reads = useTeamReads(deskId);
  const location = useLocation();
  const feed = useWorkFeed();
  const workerOf = useWorkerLookup();
  const { roster } = useTeamIdentity();
  const rosterEntry = roster.find((entry) => entry.id === deskId) ?? null;
  const settingsParam = params.get("settings");
  const isAdmin = useIsWorkspaceAdmin(false);
  // Systems live in Settings behind `manage-repos`: anyone else reads the name as plain text.
  const canManageRepos = useCan("manage-repos", false);
  const [addingPeople, setAddingPeople] = useState(false);
  const now = Date.now();

  const name = team.team?.name ?? rosterEntry?.name ?? "";
  const manageable = canManagePeople({
    isAdmin,
    viewerId: viewerUserId,
    publisherUserId: team.team?.publisherUserId,
  });
  // The folded Ask bar names the Team too, so it is plain the chat is about it.
  useDockBinding(name ? { scope: teamScope(deskId, name), context: name } : null);

  const items = useMemo(
    () => feed.items.filter((item) => item.deskId === deskId && !item.archivedAt),
    [feed.items, deskId],
  );
  const rows = useMemo(() => foldPipelineRows(items), [items]);
  const groups = useMemo(() => teamQueueGroups(rows, now), [rows, now]);
  const needsOrder = useMemo(() => {
    const needs = groups.find((group) => group.group === "needs");
    return needs ? needsOrderOf(needs) : [];
  }, [groups]);

  // The search keeps each group's matches, and reaches the Team's archived
  // tickets too. `?q=` is Add tickets' own search, so the queue's is `?find=`.
  const find = params.get("find") ?? "";
  const search = useMemo(() => parseWorkSearch(find), [find]);
  const shown = useMemo(
    () => (search ? searchQueueGroups(groups, search) : groups),
    [groups, search],
  );
  const allArchived = useMemo(
    () => archivedRows(feed.items.filter((item) => item.deskId === deskId)),
    [feed.items, deskId],
  );
  const archived = useMemo(
    () => (search ? allArchived.filter((row) => rowMatchesSearch(row, search)) : allArchived),
    [allArchived, search],
  );
  // The Status filter keeps only the chosen groups; none chosen is the queue as it is.
  const status = useStatusFilter({ shown, archived, searching: search != null });
  const { filtering } = status;
  const [archivedPage, setArchivedPage] = useState(1);
  useEffect(() => setArchivedPage(1), [find, status.param]);
  const setFind = (value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("find", value);
        else next.delete("find");
        next.delete("done");
        return next;
      },
      { replace: true },
    );
  const setDonePage = (page: number) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page > 1) next.set("done", String(page));
        else next.delete("done");
        return next;
      },
      { replace: true },
    );

  const backlog = attachedBacklogs(team.attachments).find((entry) => entry.kind !== "repo") ?? null;
  const repos = attachedRepos(team.attachments);
  const sourceWord = backlog ? (BACKLOG_SOURCE_WORD[backlog.source] ?? backlog.source) : null;
  const filter = backlog ? filterTextOf(backlog) : null;
  const matches = useFilterMatches(deskId, filter, backlog?.source ?? null);
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const pickedToday = useMemo(
    () =>
      items
        .filter((item) => proposedSince(item, startOfToday))
        .map((item) => ({ id: item.id, identifier: item.identifier, title: item.title })),
    [items, startOfToday],
  );
  const picked = rosterEntry?.pickedToday;

  const propose = lineOf(team.jobs, "pm.propose");
  // The same read What Bailey sees makes: when it holds nothing to pick, the
  // header says so and why, without the sheet having to be opened.
  const pickList = usePickList(deskId, propose != null, filter);
  const nothingToPick = nothingToPickLine(pickList.list);
  // A Team Bailey picks for with no backlog reads every open ticket; it still
  // gets a source line, so What Bailey sees is one click away.
  const source = backlog
    ? `${sourceWord} · ${backlogCardTitle(backlog).replace(/^Jira /, "")}`
    : null;
  const sourceLabel = source ?? (propose ? "Every open ticket" : null);
  const plan = lineOf(team.jobs, "staff.plan");
  const build = lineOf(team.jobs, "builder.build");
  const review = lineOf(team.jobs, "reviewer.review_pr");
  const openSettings = (
    section: SettingsSection | typeof SETTINGS_PEOPLE_ANCHOR | typeof SETTINGS_CONNECTIONS_ANCHOR,
  ) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("settings", section);
      next.delete("sees");
      return next;
    });
  const closeSettings = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("settings");
        return next;
      },
      { replace: true },
    );
  const toSettings = (section: SettingsSection) => () => openSettings(section);
  // `?settings=people` opens the Team section scrolled to its People;
  // `?settings=connections` to what it reads.
  const atPeople = settingsParam === SETTINGS_PEOPLE_ANCHOR;
  const atConnections = settingsParam === SETTINGS_CONNECTIONS_ANCHOR;
  const settingsAt = atPeople || atConnections ? "team" : sectionFor(settingsParam, team.jobs);
  // A retired line page's address names a line; once the Team is read it reads as the Agent's section.
  useEffect(() => {
    if (
      settingsParam &&
      !atPeople &&
      !atConnections &&
      team.jobs.length > 0 &&
      settingsParam !== settingsAt
    ) {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("settings", settingsAt);
          return next;
        },
        { replace: true },
      );
    }
  }, [settingsParam, atPeople, settingsAt, team.jobs.length, setParams]);

  const sheet = useQueueSheet({
    items,
    nextAfter: (id) => {
      const others = needsOrder.filter((entry) => entry !== id);
      if (others.length === 0) return null;
      const at = needsOrder.indexOf(id);
      return needsOrder.slice(at + 1).find((entry) => entry !== id) ?? others[0] ?? null;
    },
    renderAdd: (close, openItem) => (
      <AddTicketsSheet
        deskId={deskId}
        teamName={name || "this Team"}
        source={source}
        filter={filter}
        picked={picked}
        planAuthority={plan?.authority ?? null}
        initialQuery={params.get("q") ?? undefined}
        onAdded={(first) => (first ? openItem(first) : close())}
        onClose={close}
      />
    ),
    renderSees: (close, openItem) => (
      <WhatBaileySees
        deskId={deskId}
        teamName={name || "this Team"}
        attachment={
          backlog
            ? { kind: backlog.kind, source: backlog.source, ref: backlog.ref, label: backlog.label }
            : null
        }
        sourceLine={source}
        schedule={
          propose?.trigger.type === "clock"
            ? formatScheduleSlot((propose.trigger as JobClockTrigger).schedule)
            : null
        }
        cap={picked?.cap ?? (propose?.filter as { perDay?: number } | undefined)?.perDay ?? null}
        pickedToday={picked ?? null}
        pickedItems={pickedToday}
        onEditFilter={() => openSettings("bailey")}
        onOpenItem={openItem}
        onClose={close}
      />
    ),
  });

  if (team.notFound) return <Navigate to="/waiting" replace />;
  if (!feed.ready && team.isLoading) {
    return <SurfaceSkeleton label="Gathering" lens="work" testId="team-queue-loading" />;
  }

  const clock =
    propose?.trigger.type === "clock"
      ? formatScheduleSlot((propose.trigger as JobClockTrigger).schedule)
      : "";
  const reviewLevel = review
    ? ((review.filter as { reviewLevel?: ReviewLevel }).reviewLevel ?? "balanced")
    : null;
  const pickedLine = pickedTodayLine(picked);
  const channel = team.channels[0]?.label ?? null;
  // What the Team reads (YAG-986): every workspace connection unless hidden.
  // A fact in the header, the panel in settings; nothing until the read lands.
  const hiddenConnections =
    reads.reads?.connections.filter((c) => c.hidden).map((c) => c.source) ?? [];
  const readsFact =
    reads.reads && reads.reads.connections.length > 0 ? (
      <SeesButton onOpen={() => openSettings(SETTINGS_CONNECTIONS_ANCHOR)} testId="team-reads-fact">
        {hiddenConnections.length === 0
          ? "reads every connection"
          : `reads all but ${hiddenConnections.map(connectionLabel).join(", ")}`}
      </SeesButton>
    ) : null;

  const sub = [
    sourceLabel ? (
      <SeesButton onOpen={sheet.openSees} testId="team-source">
        {sourceLabel}
        {filter && (
          <>
            {" · "}
            <b title={filter}>{truncateFilter(filter)}</b>
          </>
        )}
        {matches != null && (
          <>
            {" · "}
            <span style={{ color: "var(--accent-active)" }}>{matches} in Jira</span>
          </>
        )}
      </SeesButton>
    ) : null,
    repos.length > 0
      ? `${repos.length} ${repos.length === 1 ? "repository" : "repositories"}`
      : null,
    // A server that sends the members gets the block under the header instead.
    team.team?.declaredSystemName && !team.team.declaredSystem ? (
      canManageRepos ? (
        <Link to="/settings/systems" className="src" data-testid="team-system">
          System: {team.team.declaredSystemName}
        </Link>
      ) : (
        <span data-testid="team-system">System: {team.team.declaredSystemName}</span>
      )
    ) : null,
    channel,
    readsFact,
  ].filter((fact): fact is NonNullable<typeof fact> => fact != null);

  const chips = (
    <>
      {propose && clock && (
        <Chip onClick={toSettings("bailey")}>
          Picks <b>{clock}</b>
        </Chip>
      )}
      {propose && picked?.cap != null && (
        <Chip onClick={toSettings("bailey")}>
          Up to <b>{picked.cap} a day</b>
        </Chip>
      )}
      {propose && (
        <Chip
          dot
          tone={propose.authority === "autonomous" ? "auto" : "sup"}
          onClick={toSettings("bailey")}
        >
          Propose <b>{authorityWord(propose.authority)}</b>
        </Chip>
      )}
      {plan && (
        <Chip
          dot
          tone={plan.authority === "autonomous" ? "auto" : "sup"}
          onClick={toSettings("reeve")}
        >
          Plan <b>{authorityWord(plan.authority)}</b>
        </Chip>
      )}
      {build && (
        <Chip
          dot
          tone={build.authority === "autonomous" ? "auto" : "sup"}
          onClick={toSettings("wright")}
        >
          Build <b>{authorityWord(build.authority)}</b>
        </Chip>
      )}
      {review && reviewLevel && (
        <Chip onClick={toSettings("proctor")}>
          Review <b>{REVIEW_LEVEL_WORD[reviewLevel]}</b>
        </Chip>
      )}
    </>
  );

  const importLine = importOutcomeLine(importOutcomeOf(location.state), trackerName(team.trackers));

  const anyWorking = groups.some((group) => group.group === "live" && group.rows.length > 0);
  const doneToday =
    groups.find((group) => group.group === "done")?.rows.filter((row) => isSettledToday(row, now))
      .length ?? 0;
  const anyMatch = archived.length > 0 || shown.some((group) => group.rows.length > 0);
  const isSelected = (row: PipelineRow) =>
    sheet.selectedId === row.anchor.id || sheet.selectedId === driverOf(row).id;
  const rowIn = (group: QueueGroup, archivedAt?: string | null) => (row: PipelineRow) => (
    <TicketRow
      key={row.anchor.id}
      row={row}
      group={group}
      workerOf={workerOf}
      viewerUserId={viewerUserId}
      now={now}
      sel={isSelected(row)}
      onOpen={(id) => sheet.openItem(id)}
      archived={archivedAt}
    />
  );
  // A split's family: the parent's row, its tickets under it (YAG-1113).
  const entryIn =
    (group: QueueGroup, parts: ReadonlyMap<string, readonly QueuePart[]>) => (row: PipelineRow) => (
      <TicketEntry
        key={row.anchor.id}
        row={row}
        group={group}
        parts={parts}
        workerOf={workerOf}
        viewerUserId={viewerUserId}
        now={now}
        isSelected={isSelected}
        onOpen={(id) => sheet.openItem(id)}
      />
    );

  const { shownCount } = status;
  const findBar = (
    <div className="qfind">
      <form
        className="findbox"
        role="search"
        onSubmit={(event) => event.preventDefault()}
        data-testid="team-search"
      >
        <Search width={14} height={14} aria-hidden />
        <input
          aria-label="Search this Team's tickets"
          placeholder={`Search ${name || "this Team"} by key, number or title`}
          value={find}
          onChange={(event) => setFind(event.target.value)}
        />
        {search && (
          <span className="n" data-testid="team-search-count">
            {shownCount} {shownCount === 1 ? "ticket" : "tickets"}
          </span>
        )}
        {find && (
          <button type="button" className="x" aria-label="Clear search" onClick={() => setFind("")}>
            ×
          </button>
        )}
      </form>
      <StatusFilterMenu
        chosen={status.statuses}
        counts={status.counts}
        onChange={status.setStatuses}
      />
    </div>
  );

  return (
    <>
      <ListHead
        title={name || "Team"}
        sub={sub}
        acts={
          <>
            {team.members.length > 0 && (
              <PeopleStack
                members={team.members}
                jobs={team.jobs}
                answerers={team.answerers}
                canManage={manageable}
                onAddPeople={() => setAddingPeople(true)}
                onManage={() => openSettings(SETTINGS_PEOPLE_ANCHOR)}
              />
            )}
            <Btn
              variant="primary"
              on={sheet.addOpen}
              onClick={sheet.openAdd}
              data-testid="team-add-tickets"
            >
              <PlusIcon />
              Add tickets
            </Btn>
            <Btn
              icon
              aria-label="Team settings"
              title="Team settings"
              data-testid="team-settings-open"
              onClick={toSettings("team")}
            >
              <Settings width={16} height={16} aria-hidden />
            </Btn>
          </>
        }
        chips={team.jobs.length > 0 ? chips : undefined}
        picked={
          pickedLine || nothingToPick ? (
            <>
              {pickedLine && <PickedFact line={pickedLine} />}
              {nothingToPick && (
                <>
                  {pickedLine && " · "}
                  <SeesButton onOpen={sheet.openSees} testId="team-nothing-to-pick">
                    {nothingToPick}
                  </SeesButton>
                </>
              )}
            </>
          ) : undefined
        }
      />
      {findBar}
      {importLine && (
        <p className="blst warn team-import" role="status" data-testid="team-import-line">
          {importLine}
        </p>
      )}
      {team.team?.declaredSystem && (
        <TeamSystemBlock system={team.team.declaredSystem} canEdit={canManageRepos} />
      )}
      <Groups>
        {feed.error && items.length === 0 ? (
          <div className="empty dashed tight" role="alert">
            <p>{feed.error}</p>
          </div>
        ) : (
          status.shownGroups.map(({ group, rows: groupRows, parts }) =>
            group === "done" ? (
              <PagedGroup
                key={group}
                label={QUEUE_GROUP_LABEL[group]}
                tone={group}
                rows={groupRows}
                page={params.get("done")}
                onPage={setDonePage}
                pagerLabel="finished tickets"
                fact={
                  !search &&
                  doneToday > 0 && (
                    <span className="today" data-testid="done-today">
                      <b>{doneToday}</b> today
                    </span>
                  )
                }
                empty={status.emptyLine(group)}
                renderRow={entryIn(group, parts)}
              />
            ) : (
              <Group
                key={group}
                label={QUEUE_GROUP_LABEL[group]}
                tone={group}
                empty={
                  group === "needs"
                    ? needsEmptyLine(search != null, anyMatch, anyWorking)
                    : status.emptyLine(group)
                }
              >
                {groupRows.map(entryIn(group, parts))}
              </Group>
            ),
          )
        )}
        {status.showArchived && (
          <PagedGroup
            label="Archived"
            rows={archived}
            page={archivedPage}
            onPage={setArchivedPage}
            pagerLabel="archived tickets"
            empty={status.emptyLine("archived")}
            renderRow={(row) => rowIn(queueGroupOf(row, now) ?? "done", row.anchor.archivedAt)(row)}
          />
        )}
        {search && !anyMatch && (
          <p className="blst quiet qhandoff">
            Not on this Team?{" "}
            <Btn size="sm" variant="secondary" onClick={() => searchStore.open(find.trim())}>
              Search every Team
            </Btn>
          </p>
        )}
        {!search && !filtering && <OffTicketWork deskId={deskId} teamName={name || "this Team"} />}
        {/* Renders nothing unless running copies are on for the workspace. */}
        {!search && !filtering && <TeamRunningSystem teamId={deskId} />}
      </Groups>
      <TeamSettingsSheet
        deskId={deskId}
        page={team}
        open={settingsParam != null && team.team != null}
        at={settingsAt}
        anchor={
          atPeople
            ? SETTINGS_PEOPLE_ANCHOR
            : atConnections
              ? SETTINGS_CONNECTIONS_ANCHOR
              : undefined
        }
        onClose={closeSettings}
      />
      {manageable && (
        <AddPeopleDialog
          open={addingPeople}
          onClose={() => setAddingPeople(false)}
          deskId={deskId}
          teamName={name}
          members={team.members}
          personal={team.team?.personal === true}
          viewerId={viewerUserId}
          onAdded={async () => {
            await team.refetch();
            // The Team may have a new name, and the people added see it in their sidebar.
            window.dispatchEvent(new Event(SUBSCRIPTIONS_CHANGED_EVENT));
          }}
        />
      )}
    </>
  );
}

/** A header fact that opens What Bailey sees: the source line, and nothing to pick. */
function SeesButton({
  onOpen,
  testId,
  children,
}: {
  onOpen: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="src"
      title="What Bailey sees"
      onClick={onOpen}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/** "<b>2 of 3</b> picked today · next pick tomorrow 09:00". */
function PickedFact({ line }: { line: string }) {
  const match = /^(\d+ of \d+|\d+)( picked today.*)$/.exec(line);
  if (!match) return <>{line}</>;
  return (
    <>
      <b>{match[1]}</b>
      {match[2]}
    </>
  );
}
